/**
 * Swiper-style hold-and-drag for Dimark sliders (featured collections + logo wall).
 *
 * Village Quality Products (Elementor/Swiper) moves the track with GPU-composited
 * `translate3d`, not scrollLeft. Native overflow scroll + scroll-snap is why
 * scrollLeft-based drag still felt laggy.
 *
 * Flow:
 *   1. pointerdown → remember scroll offset
 *   2. after small move → lock that offset into translate3d on `.slider__grid`
 *   3. pointermove → update translate (RAF) with edge rubber-band
 *   4. pointerup → freeMode momentum on the transform, then commit to scrollLeft
 *
 * Touch keeps native overflow scrolling.
 */
(function () {
  'use strict';

  // Keep this high enough that a normal product-card click (often on the
  // stretched .card-link::after) does not become a drag and get swallowed.
  var MOVE_THRESHOLD = 10;
  var LINK_MOVE_THRESHOLD = 14;
  var FRICTION = 0.953;
  var MIN_VELOCITY = 0.03; // px/ms
  var VELOCITY_SAMPLES = 6;
  var RESISTANCE = 0.55;

  function prefersFinePointer() {
    return window.matchMedia('(pointer: fine)').matches;
  }

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function enhance(slider) {
    if (!slider || slider.dataset.dragEnhanced === 'v4') return;
    slider.dataset.dragEnhanced = 'v4';

    var grid = slider.querySelector('.slider__grid');
    if (!grid) return;

    var carousel = slider.closest('carousel-slider');
    var active = false;
    var dragging = false;
    var pointerId = null;
    var startX = 0;
    var pressScroll = 0;
    var startTranslate = 0;
    var currentTranslate = 0;
    var minTranslate = 0;
    var maxTranslate = 0;
    var pendingX = null;
    var rafId = 0;
    var coastRaf = 0;
    var velocity = 0;
    var samples = [];
    var savedSnap = '';
    var savedOverflow = '';
    var startedOnLink = false;
    var moveThreshold = MOVE_THRESHOLD;

    function contentMaxScroll() {
      return Math.max(0, Math.max(grid.scrollWidth, slider.scrollWidth) - slider.clientWidth);
    }

    function cancelCoast() {
      if (coastRaf) {
        cancelAnimationFrame(coastRaf);
        coastRaf = 0;
      }
    }

    function setGrabbing(on) {
      slider.classList.toggle('is-grabbing', !!on);
      slider.classList.toggle('is-dragging', !!(on && dragging));
    }

    function disarmThemeDrag() {
      if (carousel) carousel.mousedown = false;
    }

    function resist(translate) {
      if (translate > minTranslate) {
        return minTranslate + (translate - minTranslate) * RESISTANCE;
      }
      if (translate < maxTranslate) {
        return maxTranslate + (translate - maxTranslate) * RESISTANCE;
      }
      return translate;
    }

    function paint(translate, withResistance) {
      currentTranslate = withResistance
        ? resist(translate)
        : clamp(translate, maxTranslate, minTranslate);
      grid.style.transform = 'translate3d(' + currentTranslate + 'px,0,0)';
    }

    function beginTransformMode(fromScroll) {
      savedSnap = slider.style.scrollSnapType;
      savedOverflow = slider.style.overflowX;

      slider.style.scrollBehavior = 'auto';
      slider.style.scrollSnapType = 'none';
      slider.scrollLeft = 0;

      startTranslate = -fromScroll;
      currentTranslate = startTranslate;
      minTranslate = 0;
      maxTranslate = -contentMaxScroll();

      grid.style.willChange = 'transform';
      grid.style.transform = 'translate3d(' + startTranslate + 'px,0,0)';
      slider.style.overflowX = 'hidden';
    }

    function commitTransformToScroll() {
      var scroll = clamp(-currentTranslate, 0, contentMaxScroll());
      grid.style.transform = '';
      grid.style.willChange = '';
      slider.style.overflowX = savedOverflow || '';
      slider.style.scrollSnapType = savedSnap || '';
      slider.scrollLeft = scroll;
      currentTranslate = 0;
      startTranslate = 0;
    }

    function flushMove() {
      rafId = 0;
      if (!active || pendingX === null) return;

      var x = pendingX;
      pendingX = null;

      samples.push({ x: x, t: performance.now() });
      if (samples.length > VELOCITY_SAMPLES) samples.shift();

      var dx = x - startX;
      if (!dragging) {
        if (Math.abs(dx) < moveThreshold) return;
        dragging = true;
        beginTransformMode(pressScroll);
        setGrabbing(true);
        // Capture only after a real drag starts so product-card <a> clicks
        // still navigate (capture on pointerdown steals the click target).
        try {
          if (pointerId != null) slider.setPointerCapture(pointerId);
        } catch (err) {
          /* ignore */
        }
      }

      var target = startTranslate + dx;

      // Lazy images that finish loading mid-drag widen the track, so refresh the
      // end stop before rubber-banding against a limit measured on a shorter grid.
      if (target < maxTranslate) maxTranslate = -contentMaxScroll();

      paint(target, true);
    }

    function computeVelocity() {
      if (samples.length < 2) return 0;
      var first = samples[0];
      var last = samples[samples.length - 1];
      var dt = last.t - first.t;
      if (dt <= 0) return 0;
      return (last.x - first.x) / dt;
    }

    function coast() {
      coastRaf = 0;

      if (Math.abs(velocity) < MIN_VELOCITY) {
        paint(currentTranslate, false);
        commitTransformToScroll();
        setGrabbing(false);
        return;
      }

      var next = currentTranslate + velocity * 16.67;

      if (next > minTranslate || next < maxTranslate) {
        paint(next > minTranslate ? minTranslate : maxTranslate, false);
        commitTransformToScroll();
        setGrabbing(false);
        return;
      }

      currentTranslate = next;
      grid.style.transform = 'translate3d(' + currentTranslate + 'px,0,0)';
      velocity *= FRICTION;
      coastRaf = requestAnimationFrame(coast);
    }

    function swallowNextClick() {
      var swallow = function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
        slider.removeEventListener('click', swallow, true);
      };
      slider.addEventListener('click', swallow, true);
      setTimeout(function () {
        slider.removeEventListener('click', swallow, true);
      }, 100);
    }

    function onPointerDown(e) {
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;
      // Logo wall keeps `inactive` so the theme carousel does not fight the
      // infinite loop, but it still needs hold-and-drag.
      if (carousel && carousel.hasAttribute('inactive') && !slider.closest('.cc-logo-wall')) return;
      if (e.target.closest('button, input, select, textarea, label, quantity-input, .qty-input')) return;

      cancelCoast();
      if (grid.style.transform && grid.style.transform !== 'none') {
        commitTransformToScroll();
      }

      active = true;
      dragging = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      pressScroll = slider.scrollLeft;
      startTranslate = -pressScroll;
      currentTranslate = startTranslate;
      samples = [{ x: e.clientX, t: performance.now() }];
      velocity = 0;
      startedOnLink = !!e.target.closest('a');
      moveThreshold = startedOnLink ? LINK_MOVE_THRESHOLD : MOVE_THRESHOLD;
      bindReleaseFallback();

      // Do not setPointerCapture / is-grabbing yet — wait until drag threshold.
      queueMicrotask(disarmThemeDrag);
    }

    function onPointerMove(e) {
      if (!active || e.pointerId !== pointerId) return;
      if (dragging) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      pendingX = e.clientX;
      if (!rafId) rafId = requestAnimationFrame(flushMove);
      disarmThemeDrag();
    }

    function onPointerUp(e) {
      if (!active || (pointerId !== null && e.pointerId !== pointerId)) return;

      unbindReleaseFallback();

      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
        if (pendingX !== null) flushMove();
      }

      var didDrag = dragging;
      if (didDrag) {
        velocity = computeVelocity();
        swallowNextClick();
      }

      active = false;
      dragging = false;
      pointerId = null;
      pendingX = null;

      try {
        if (e.pointerId != null) slider.releasePointerCapture(e.pointerId);
      } catch (err) {
        /* ignore */
      }

      disarmThemeDrag();

      if (!didDrag) {
        setGrabbing(false);
        return;
      }

      if (currentTranslate > minTranslate || currentTranslate < maxTranslate) {
        paint(currentTranslate, false);
      }

      if (Math.abs(velocity) >= MIN_VELOCITY) {
        coastRaf = requestAnimationFrame(coast);
      } else {
        commitTransformToScroll();
        setGrabbing(false);
      }
    }

    /**
     * Safety net for releases the slider itself never sees. Bound only for the
     * duration of a drag so no window listener outlives the section. onPointerUp
     * bails out once `active` is false, so whichever listener runs first wins.
     */
    function bindReleaseFallback() {
      window.addEventListener('pointerup', onPointerUp, true);
      window.addEventListener('pointercancel', onPointerUp, true);
      window.addEventListener('blur', forceRelease);
    }

    function unbindReleaseFallback() {
      window.removeEventListener('pointerup', onPointerUp, true);
      window.removeEventListener('pointercancel', onPointerUp, true);
      window.removeEventListener('blur', forceRelease);
    }

    /**
     * Last-resort recovery. beginTransformMode puts the slider into
     * `overflow-x: hidden` with `scrollLeft` at 0, and only a pointer release
     * restores it. If that release never arrives -- setPointerCapture threw and
     * the button came up off-element, or the tab lost focus mid-drag -- the
     * carousel would stay frozen on the first item until a reload.
     */
    function forceRelease() {
      unbindReleaseFallback();
      if (!active) return;

      cancelCoast();
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }

      active = false;
      dragging = false;
      pointerId = null;
      pendingX = null;

      if (grid.style.transform && grid.style.transform !== 'none') {
        commitTransformToScroll();
      }

      setGrabbing(false);
      disarmThemeDrag();
    }

    slider.style.scrollBehavior = 'auto';
    slider.style.scrollSnapType = 'none';

    slider.addEventListener('pointerdown', onPointerDown);
    slider.addEventListener('pointermove', onPointerMove, true);
    slider.addEventListener('pointerup', onPointerUp, true);
    slider.addEventListener('pointercancel', onPointerUp, true);

    slider.addEventListener(
      'mouseleave',
      function (e) {
        if (!active) return;
        e.stopImmediatePropagation();
        disarmThemeDrag();
      },
      true
    );

    slider.addEventListener(
      'mousemove',
      function (e) {
        if (!active) return;
        e.stopImmediatePropagation();
        disarmThemeDrag();
      },
      true
    );

    slider.addEventListener(
      'dragstart',
      function (e) {
        e.preventDefault();
      },
      true
    );
  }

  function init(root) {
    if (!prefersFinePointer()) return;
    (root || document).querySelectorAll('.slider--drag-fix').forEach(enhance);
  }

  window.initDimarkDragScroll = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!node || node.nodeType !== 1) return;
          if (node.matches && node.matches('.slider--drag-fix')) enhance(node);
          if (node.querySelectorAll) node.querySelectorAll('.slider--drag-fix').forEach(enhance);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
